import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAssetUpload } from '../hooks/use-asset-upload';
import type { UploadProgress } from '../hooks/use-asset-upload';
import type { Folder, LibraryAsset } from '../types';
import { buildFolderTree, flattenFolderTree } from '../utils/folder-tree';

type UploadZoneProps = {
  enterpriseId: string;
  folderId: string | null;
  folders: Folder[];
  onCreateFolder: (name: string, parentId?: string | null) => Promise<void> | void;
  onClose: () => void;
  /** Called with every successfully-uploaded asset (including dedupe hits) when the zone closes. */
  onComplete: (uploaded: LibraryAsset[]) => void;
};

type PendingFile = {
  id: string;
  file: File;
  /** Relative path from the dropped folder root, e.g. "Banners/Holiday" */
  folderPath: string | null;
  /** Resolved folder id after auto-creation */
  resolvedFolderId: string | null;
  preview: string;
  progress: UploadProgress;
  done: boolean;
  alreadyInLibrary: boolean;
  /** The uploaded (or deduped) asset once `done` -- undefined until then or on error. */
  resultAsset?: LibraryAsset;
};

const ACCEPTED_MIME_PREFIXES = ['image/', 'video/'];
const MAX_BYTES = 100 * 1024 * 1024;

const isAccepted = (file: File): boolean =>
  ACCEPTED_MIME_PREFIXES.some((p) => file.type.startsWith(p)) && file.size > 0 && file.size <= MAX_BYTES;

// --- Folder-drop helpers ---------------------------------------------------

type FileWithPath = { file: File; relativePath: string | null };

/** Read a DataTransferItem as a FileSystemEntry tree and collect all files */
async function readEntryTree(entry: FileSystemEntry, basePath: string): Promise<FileWithPath[]> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
    const parentDir = basePath || null;
    return [{ file, relativePath: parentDir }];
  }

  if (entry.isDirectory) {
    const dirReader = (entry as FileSystemDirectoryEntry).createReader();
    const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      const all: FileSystemEntry[] = [];
      const readBatch = () => {
        dirReader.readEntries((batch) => {
          if (batch.length === 0) {
            resolve(all);
          } else {
            all.push(...batch);
            readBatch();
          }
        }, reject);
      };
      readBatch();
    });

    const childPath = basePath ? `${basePath}/${entry.name}` : entry.name;
    const nested = await Promise.all(entries.map((e) => readEntryTree(e, childPath)));
    return nested.flat();
  }

  return [];
}

async function extractFilesFromDrop(e: React.DragEvent): Promise<FileWithPath[]> {
  const { items } = e.dataTransfer;
  if (!items || items.length === 0) {
    // Fallback: plain file drop
    return Array.from(e.dataTransfer.files).map((file) => ({ file, relativePath: null }));
  }

  const entries: FileSystemEntry[] = [];
  Array.from(items).forEach((item) => {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
    }
  });

  if (entries.length === 0) {
    return Array.from(e.dataTransfer.files).map((file) => ({ file, relativePath: null }));
  }

  const results = await Promise.all(entries.map((entry) => readEntryTree(entry, '')));
  return results.flat();
}

/** Given "Banners/Holiday", returns ["Banners", "Banners/Holiday"] */
function allPrefixes(path: string): string[] {
  const parts = path.split('/');
  const result: string[] = [];
  for (let i = 1; i <= parts.length; i++) {
    result.push(parts.slice(0, i).join('/'));
  }
  return result;
}

// ---------------------------------------------------------------------------

export function UploadZone({
  enterpriseId,
  folderId,
  folders,
  onCreateFolder,
  onClose,
  onComplete,
}: UploadZoneProps): JSX.Element {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [creatingFolders, setCreatingFolders] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const { upload } = useAssetUpload(enterpriseId);

  const folderOptions = useMemo(() => flattenFolderTree(buildFolderTree(folders)), [folders]);

  const [selectedFolderId, setSelectedFolderId] = useState<string>(folderId ?? '');

  const updateFile = useCallback((id: string, patch: Partial<PendingFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  const startUpload = useCallback(
    async (pf: PendingFile) => {
      try {
        const targetFolderId = pf.resolvedFolderId ?? (selectedFolderId || null);
        const result = await upload(pf.file, {
          folderId: targetFolderId,
          onProgress: (progress) => updateFile(pf.id, { progress }),
        });
        updateFile(pf.id, {
          progress: { stage: 'done', percent: 100 },
          done: true,
          alreadyInLibrary: result.alreadyInLibrary,
          resultAsset: result.asset,
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        updateFile(pf.id, { progress: { stage: 'error', percent: 0, error: message } });
      }
    },
    [selectedFolderId, updateFile, upload]
  );

  /**
   * Given a set of relative folder paths (e.g. "Banners", "Banners/Holiday"),
   * creates any that don't already exist and returns a map from path → folderId.
   */
  const ensureFolderStructure = useCallback(
    async (paths: Set<string>, parentId: string | null): Promise<Map<string, string>> => {
      if (paths.size === 0) return new Map();

      // Expand all intermediate paths
      const allPaths = new Set<string>();
      paths.forEach((p) => {
        allPrefixes(p).forEach((prefix) => {
          allPaths.add(prefix);
        });
      });

      // Sort by depth so parents are created before children
      const sorted = Array.from(allPaths).sort((a, b) => a.split('/').length - b.split('/').length);

      // Build a lookup of existing folders by parent chain
      const pathToId = new Map<string, string>();

      let promiseChain = Promise.resolve();
      sorted.forEach((fullPath) => {
        promiseChain = promiseChain.then(async () => {
          const parts = fullPath.split('/');
          const folderName = parts[parts.length - 1];
          const parentPath = parts.slice(0, -1).join('/');
          const resolvedParent = parentPath ? pathToId.get(parentPath) ?? null : parentId;

          // Check if this folder already exists
          const existing = folders.find((f) => f.name === folderName && f.parentId === resolvedParent);

          if (existing) {
            pathToId.set(fullPath, existing.id);
          } else {
            // Create the folder — onCreateFolder triggers a refetch, but we need the ID.
            // We'll call the mutation and then find the new folder in the updated list.
            await onCreateFolder(folderName, resolvedParent);

            // After creation + refetch we won't have the ID immediately in `folders` prop
            // (it's stale in this closure). We'll do a best-effort match after all creates.
            pathToId.set(fullPath, `pending:${fullPath}`);
          }
        });
      });
      await promiseChain;

      return pathToId;
    },
    [folders, onCreateFolder]
  );

  const processFilesWithPaths = useCallback(
    async (filesWithPaths: FileWithPath[]) => {
      const accepted = filesWithPaths.filter((fp) => isAccepted(fp.file));
      if (accepted.length === 0) return;

      // Collect unique folder paths
      const folderPaths = new Set<string>();
      accepted.forEach((fp) => {
        if (fp.relativePath) folderPaths.add(fp.relativePath);
      });

      // Create missing folders
      let pathToId = new Map<string, string>();
      if (folderPaths.size > 0) {
        setCreatingFolders(true);
        try {
          pathToId = await ensureFolderStructure(folderPaths, selectedFolderId || null);
        } finally {
          setCreatingFolders(false);
        }
      }

      // Build pending files
      const newFiles: PendingFile[] = accepted.map((fp) => {
        const resolvedFolderId = fp.relativePath ? pathToId.get(fp.relativePath) ?? null : null;

        return {
          id: `${fp.file.name}-${fp.file.size}-${fp.file.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
          file: fp.file,
          folderPath: fp.relativePath,
          resolvedFolderId: resolvedFolderId?.startsWith('pending:') ? null : resolvedFolderId,
          preview: URL.createObjectURL(fp.file),
          progress: { stage: 'hashing' as const, percent: 0 },
          done: false,
          alreadyInLibrary: false,
        };
      });

      setFiles((prev) => [...prev, ...newFiles]);
      newFiles.forEach((pf) => void startUpload(pf));
    },
    [ensureFolderStructure, selectedFolderId, startUpload]
  );

  const processFiles = useCallback(
    (fileList: FileList) => {
      const filesWithPaths: FileWithPath[] = Array.from(fileList).map((file) => ({
        file,
        relativePath: null,
      }));
      void processFilesWithPaths(filesWithPaths);
    },
    [processFilesWithPaths]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const filesWithPaths = await extractFilesFromDrop(e);
      void processFilesWithPaths(filesWithPaths);
    },
    [processFilesWithPaths]
  );

  const handleFolderInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;

      // webkitRelativePath gives "FolderName/subfolder/file.jpg"
      const filesWithPaths: FileWithPath[] = Array.from(fileList).map((file) => {
        const parts = file.webkitRelativePath.split('/');
        // Remove the file name, keep the directory path
        const dirParts = parts.slice(0, -1);
        return {
          file,
          relativePath: dirParts.length > 0 ? dirParts.join('/') : null,
        };
      });

      void processFilesWithPaths(filesWithPaths);
    },
    [processFilesWithPaths]
  );

  const handleRetry = useCallback(
    (pf: PendingFile) => {
      updateFile(pf.id, { progress: { stage: 'hashing', percent: 0 }, done: false });
      void startUpload(pf);
    },
    [startUpload, updateFile]
  );

  const handleClose = useCallback(() => {
    const uploaded = files.map((f) => f.resultAsset).filter((asset): asset is LibraryAsset => asset != null);
    if (uploaded.length > 0) onComplete(uploaded);
    onClose();
  }, [files, onComplete, onClose]);

  const allDone = files.length > 0 && files.every((f) => f.done || f.progress.stage === 'error');

  let actionButtonLabel = 'Uploading...';
  if (files.length === 0) {
    actionButtonLabel = 'Close';
  } else if (allDone) {
    actionButtonLabel = 'Done';
  }

  return (
    <Overlay onClick={handleClose} role='presentation'>
      <Modal onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
        <ModalHeader>
          <Title>Upload Assets</Title>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <FolderSelect>
            <FolderLabel>Upload to folder:</FolderLabel>
            <SelectInput value={selectedFolderId} onChange={(e) => setSelectedFolderId(e.target.value)}>
              <option value=''>Root (no folder)</option>
              {folderOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {'\u00A0\u00A0'.repeat(f.depth)}
                  {f.name}
                </option>
              ))}
            </SelectInput>
          </FolderSelect>

          <DropArea
            $dragging={dragging}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <DropIcon>⬆</DropIcon>
            <DropText>Drop files or folders here, or</DropText>
            <ButtonRow>
              <BrowseButton onClick={() => inputRef.current?.click()}>Browse Files</BrowseButton>
              <BrowseFolderButton onClick={() => folderInputRef.current?.click()}>Browse Folder</BrowseFolderButton>
            </ButtonRow>
            <input
              ref={inputRef}
              type='file'
              accept='image/*,video/*'
              multiple
              hidden
              onChange={(e) => e.target.files && processFiles(e.target.files)}
            />
            <input
              ref={folderInputRef}
              type='file'
              // eslint-disable-next-line react/no-unknown-property
              {...({ webkitdirectory: '' } as any)}
              multiple
              hidden
              onChange={handleFolderInput}
            />
            <DropHint>
              Accepts images (JPG, PNG, SVG, WebP, GIF) and video (MP4, WebM). Max 100MB.
              <br />
              Drop a folder to mirror its structure automatically.
            </DropHint>
          </DropArea>

          {creatingFolders && <StatusMessage>Creating folder structure…</StatusMessage>}

          {files.length > 0 && (
            <FileList>
              {files.map((pf) => (
                <FileRow key={pf.id}>
                  <FilePreview src={pf.preview} alt='' />
                  <FileInfo>
                    <FileName>
                      {pf.folderPath && <FolderPathLabel>{pf.folderPath}/</FolderPathLabel>}
                      {pf.file.name}
                    </FileName>
                    <ProgressBar>
                      <ProgressFill $pct={pf.progress.percent} $error={pf.progress.stage === 'error'} />
                    </ProgressBar>
                  </FileInfo>
                  <ProgressMeta>{renderProgressMeta(pf, () => handleRetry(pf))}</ProgressMeta>
                </FileRow>
              ))}
            </FileList>
          )}
        </ModalBody>
        <ModalFooter>
          <CancelButton onClick={handleClose}>Close</CancelButton>
          <ActionButton onClick={handleClose} disabled={!allDone && files.length > 0}>
            {actionButtonLabel}
          </ActionButton>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
}

function labelForStage(p: UploadProgress): string {
  switch (p.stage) {
    case 'hashing':
      return `Hashing ${p.percent}%`;
    case 'requesting':
      return 'Preparing…';
    case 'uploading':
      return `${p.percent}%`;
    case 'finalizing':
      return 'Saving…';
    case 'done':
    case 'error':
    default:
      return '';
  }
}

function renderProgressMeta(pf: PendingFile, onRetry: () => void): JSX.Element {
  if (pf.progress.stage === 'error') {
    return <RetryButton onClick={onRetry}>Retry</RetryButton>;
  }
  if (pf.done) {
    return pf.alreadyInLibrary ? <DupeBadge>Already in library</DupeBadge> : <DoneBadge>Done</DoneBadge>;
  }
  return <ProgressText>{labelForStage(pf.progress)}</ProgressText>;
}

// --- Styled components ------------------------------------------------------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e1e6eb;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #121516;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6d747b;
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const FolderSelect = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const FolderLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #6d747b;
  white-space: nowrap;
`;

const SelectInput = styled.select`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  font-size: 13px;
  color: #121516;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #0b99e6;
  }
`;

const DropArea = styled.div<{ $dragging: boolean }>`
  border: 2px dashed ${({ $dragging }) => ($dragging ? '#0b99e6' : '#c4cdd5')};
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
  background: ${({ $dragging }) => ($dragging ? '#e8f4fd' : '#f7f8fa')};
  transition: border-color 0.15s, background 0.15s;
`;

const DropIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const DropText = styled.div`
  font-size: 14px;
  color: #6d747b;
  margin-bottom: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const BrowseButton = styled.button`
  background: #0b99e6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0988cf;
  }
`;

const BrowseFolderButton = styled.button`
  background: #ffffff;
  color: #0b99e6;
  border: 1px solid #0b99e6;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e8f4fd;
  }
`;

const DropHint = styled.div`
  font-size: 11px;
  color: #6d747b;
  margin-top: 12px;
  line-height: 1.5;
`;

const StatusMessage = styled.div`
  font-size: 13px;
  color: #0b99e6;
  font-weight: 600;
  text-align: center;
  padding: 12px 0 4px;
`;

const FileList = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilePreview = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 12px;
  color: #121516;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
`;

const FolderPathLabel = styled.span`
  color: #6d747b;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #e1e6eb;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $error: boolean }>`
  height: 100%;
  width: ${({ $pct, $error }) => ($error ? 100 : $pct)}%;
  background: ${({ $error }) => ($error ? '#c62828' : '#0b99e6')};
  transition: width 0.3s ease;
`;

const ProgressMeta = styled.div`
  min-width: 100px;
  text-align: right;
  font-size: 11px;
`;

const ProgressText = styled.span`
  color: #6d747b;
`;

const DoneBadge = styled.span`
  color: #137333;
  font-weight: 600;
`;

const DupeBadge = styled.span`
  color: #7c3aed;
  font-weight: 600;
`;

const RetryButton = styled.button`
  background: none;
  border: 1px solid #c62828;
  color: #c62828;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  background: #f7f8fa;
  border-top: 1px solid #e1e6eb;
  flex-shrink: 0;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #c4cdd5;
  border-radius: 6px;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: #121516;
  cursor: pointer;

  &:hover {
    background: #f7f8fa;
  }
`;

const ActionButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #0b99e6;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #0988cf;
  }

  &:disabled {
    background: #c4cdd5;
    cursor: not-allowed;
  }
`;
